import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import type { IRecentObject, IStateObject } from "app/@types/userdata";

import { AVAILABLE_KEYWORD_LIST_TYPES } from "./constants";
import { createAiTopicEntity, createKeywordGroupEntity, createSearchTermEntity } from "./entities";
import type { KeywordListEntity } from "./types";
import { parseKeywordListId } from "./utils";

export interface RecentKeywordListEntity {
    recentId: string;
    updatedTime: string;
    entity: KeywordListEntity;
}

/**
 * Extracts entity from params.id array (new URL-based format)
 * Demand Analysis URL format (v3)
 */
const extractEntityFromParams = (data: IStateObject): KeywordListEntity | null => {
    if (!data.params?.id?.length) {
        return null;
    }

    const lastId = data.params.id.at(-1);
    if (!lastId || typeof lastId !== "string") {
        return null;
    }

    return parseKeywordListId(lastId);
};

/**
 * Extracts entity from entity-specific fields (searchTerm, keywordGroup, aiTopic)
 * Demand Analysis URL format (v2)
 */
const extractEntityFromFields = (data: IStateObject): KeywordListEntity | null => {
    const country = data.country ?? WORLDWIDE_COUNTRY_ID;

    if (data.searchTerm?.length) {
        const seed = data.searchTerm[0];
        return createSearchTermEntity({ type: "SearchTerm", seed, country }, seed);
    }

    if (data.keywordGroup?.length) {
        const keywordGroupId = data.keywordGroup[0];
        return createKeywordGroupEntity({ type: "KeywordGroup", keywordGroupId }, keywordGroupId);
    }

    if (data.aiTopic?.length) {
        const seed = data.aiTopic[0];
        return createAiTopicEntity({ type: "AiTopic", seed, country }, seed);
    }

    return null;
};

/**
 * Extracts entity from legacy format (keyword field with special handling)
 * Demand Analysis URL format (v1)
 */
const extractEntityFromLegacyFormat = (data: IStateObject): KeywordListEntity | null => {
    const { keyword, params, mainItem } = data;
    const country = data.country ?? WORLDWIDE_COUNTRY_ID;

    if (params?.isTopic === "true" && keyword) {
        return null;
    }

    // Keyword group indicated by "*" prefix
    if (keyword?.startsWith("*")) {
        const keywordGroupId = keyword.substring(1);
        return createKeywordGroupEntity({ type: "KeywordGroup", keywordGroupId }, keywordGroupId);
    }

    // Search term indicated by params.isTerm or just a plain keyword
    if (params?.isTerm === "true" && keyword) {
        return createSearchTermEntity({ type: "SearchTerm", seed: keyword, country }, keyword);
    }

    // Plain keyword without special flags - treat as search term
    if (keyword) {
        return createSearchTermEntity({ type: "SearchTerm", seed: keyword, country }, keyword);
    }

    // Fallback to mainItem if available
    if (mainItem) {
        return createSearchTermEntity({ type: "SearchTerm", seed: mainItem, country }, mainItem);
    }

    return null;
};

const parseRecentDataToEntity = (data: IStateObject): KeywordListEntity | null => {
    if (data.type !== "keyword") {
        return null;
    }

    // Try parsing from params.id first (new format)
    const entityFromParams = extractEntityFromParams(data);
    if (entityFromParams) {
        return entityFromParams;
    }

    // Try entity-specific fields
    const entityFromFields = extractEntityFromFields(data);
    if (entityFromFields) {
        return entityFromFields;
    }

    // Try legacy format
    return extractEntityFromLegacyFormat(data);
};

/**
 * Parses a single recent search item and extracts the KeywordListEntity.
 * Handles multiple formats:
 * - New format (v3): params.id array containing serialized keyword list IDs
 * - Entity-specific fields (v2): searchTerm, keywordGroup, aiTopic arrays
 * - Legacy format (v1): keyword field with special prefixes (e.g., "*" for keyword groups)
 *
 * @returns {KeywordListEntity | null}
 */
const parseRecentToEntity = (recent: IRecentObject): KeywordListEntity | null => {
    const { data } = recent;

    if (data.type !== "keyword") {
        return null;
    }

    return parseRecentDataToEntity(data);
};

/**
 * Parses recent searches into enriched entities that include both the parsed
 * KeywordListEntity and essential metadata from the original recent item.
 *
 * @param {IRecentObject[]} recents - Array of recent objects to parse
 * @returns {RecentKeywordListEntity[]} Array of RecentKeywordListEntity objects with entity and recent metadata
 *
 * @example
 * const recents = RecentService.getRecents();
 * const recentEntities = getRecentKeywordListEntities(recents);
 *
 * // Filter by entity type
 * const searchTerms = recentEntities.filter((r) => r.entity.type === "searchTerm");
 * const keywordGroups = recentEntities.filter((r) => r.entity.type === "keywordGroup");
 *
 * // Access recent metadata
 * const sortedByDate = recentEntities.toSorted((a, b) => b.updatedTime.localeCompare(a.updatedTime));
 *
 * // Consumer handles ownership check
 * const ownGroups = keywordGroups.filter((r) => userGroupIds.has(r.entity.id));
 */
export const getRecentKeywordListEntities = (recents: IRecentObject[]): RecentKeywordListEntity[] => {
    const recentEntities = recents
        .map((recent) => {
            const entity = parseRecentToEntity(recent);

            if (!entity) {
                return null;
            }

            // Filter out unsupported entity types (topic, searchTerm)
            if (!AVAILABLE_KEYWORD_LIST_TYPES.includes(entity.type)) {
                return null;
            }

            return {
                recentId: recent.id,
                updatedTime: recent.updatedTime,
                entity,
            } satisfies RecentKeywordListEntity;
        })
        .filter((entity) => entity !== null);

    return recentEntities;
};
